import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Award, Users, Leaf, ChevronRight, Check } from 'lucide-react';
import { images, restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const PresentationPage = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Heart, title: t('presentation', 'passion'), description: t('presentation', 'passionDesc') },
    { icon: Award, title: t('presentation', 'quality'), description: t('presentation', 'qualityDesc') },
    { icon: Users, title: t('presentation', 'conviviality'), description: t('presentation', 'convivialityDesc') },
    { icon: Leaf, title: t('presentation', 'freshness'), description: t('presentation', 'freshnessDesc') },
  ];

  const bullets = t('presentation', 'bullets');

  return (
    <div className="min-h-screen pt-24">
      <section className="relative py-20 bg-[#F5E6D3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6 leading-tight">
                {t('presentation', 'heroTitle')}
              </h1>
              <p className="text-xl text-[#0f172a]/80 mb-8 leading-relaxed">
                {t('presentation', 'heroText')}
              </p>
              <Link to="/menu" className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg">
                {t('presentation', 'discoverMenu')}
                <ChevronRight size={20} />
              </Link>
            </div>
            <div className="relative">
              <img src={images.hero} alt="Cuisine libanaise" className="w-full h-auto rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="font-serif text-3xl font-bold text-[#D4A853]">20+</p>
                <p className="text-[#64748b]">{t('presentation', 'yearsExp')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-[#0f172a] mb-4">{t('presentation', 'values')}</h2>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto">{t('presentation', 'valuesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-8 rounded-xl bg-[#f8fafc] hover:bg-[#E8D4BE]/30 transition-all duration-300">
                <div className="w-16 h-16 bg-[#F5E6D3] rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon size={28} className="text-[#8B4513]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#0f172a] mb-4">{value.title}</h3>
                <p className="text-[#64748b] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img src={images.mezze1} alt="Mezzés" className="w-full h-48 object-cover rounded-xl shadow-lg" />
                <img src={images.grill1} alt="Grillades" className="w-full h-48 object-cover rounded-xl shadow-lg mt-8" />
                <img src={images.falafel} alt="Falafels" className="w-full h-48 object-cover rounded-xl shadow-lg" />
                <img src={images.rice} alt="Kabsa" className="w-full h-48 object-cover rounded-xl shadow-lg mt-8" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-serif text-4xl font-bold text-[#0f172a] mb-6">{t('presentation', 'ourStory')}</h2>
              <p className="text-[#64748b] mb-6 leading-relaxed text-lg">{t('presentation', 'storyP1')}</p>
              <p className="text-[#64748b] mb-8 leading-relaxed text-lg">{t('presentation', 'storyP2')}</p>
              <div className="space-y-4">
                {(Array.isArray(bullets) ? bullets : []).map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-[#8B4513]" />
                    </div>
                    <span className="text-[#0f172a]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5E6D3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-4xl font-bold text-[#0f172a] mb-6">{t('presentation', 'quote')}</h2>
            <p className="text-xl text-[#0f172a]/80 mb-8 leading-relaxed">{t('presentation', 'quoteText')}</p>
            <p className="text-lg text-[#8B4513] font-semibold">{t('presentation', 'quoteAuthor')}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl font-bold text-[#0f172a] mb-6">{t('presentation', 'visitUs')}</h2>
          <p className="text-xl text-[#64748b] mb-10">{t('presentation', 'visitText')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation" className="bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg">
              {t('presentation', 'bookTable')}
            </Link>
            <Link to="/contact" className="bg-transparent text-[#0f172a] border-2 border-[#0f172a] px-8 py-4 rounded-md font-semibold hover:bg-[#0f172a] hover:text-white transition-all duration-200">
              {t('presentation', 'contactUs')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PresentationPage;
